import React from "react";
import { Link } from "react-router-dom";
import { FiArrowDown, FiArrowRight } from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

function About() {
  const fallbackProduct = productData[0];
  const findProduct = (predicate) => productData.find(predicate) || fallbackProduct;
  const leadProduct = findProduct((product) => product.gender === "Women" && product.category === "Dresses" && product.tags.includes("new-in"));
  const mensShirt = findProduct((product) => product.gender === "Men" && product.category === "Shirts");
  const coastalLayer = findProduct((product) => product.category === "Jackets");
  const linenOnePiece = findProduct((product) => product.category === "Jumpsuits");
  const preorderDress = findProduct((product) => product.category === "Dresses" && product.tags.includes("pre-order") && product.id !== leadProduct.id);
  const saleDress = findProduct((product) => product.category === "Dresses" && product.tags.includes("sale"));
  const tailoredBottom = findProduct((product) => product.category === "Trousers");
  const mensKnit = findProduct((product) => product.gender === "Men" && product.category === "Knitwear");

  const principles = [
    {
      number: "01",
      title: "Materials with a lighter wake",
      copy: "We favour organic cotton, European linen, recycled fibres and smooth botanical fabrics. Every fibre is chosen for feel, function and a lower-impact future.",
      image: linenOnePiece.image,
      alt: `${linenOnePiece.name} linen jumpsuit photographed among coastal dunes`,
    },
    {
      number: "02",
      title: "Close-knit craft",
      copy: "Our collections are designed in Barcelona and made with trusted partners in Portugal and Spain, keeping knowledge, dialogue and production close.",
      image: mensShirt.image,
      alt: `${mensShirt.name} linen shirt photographed beside the sea`,
    },
    {
      number: "03",
      title: "Demand before volume",
      copy: "Pre-order helps us listen before we make. Producing in considered quantities means less excess stock and more time spent getting every detail right.",
      image: preorderDress.image,
      alt: `${preorderDress.name} dress in a coastal editorial`,
    },
  ];

  const process = [
    ["01", "Begin with the fibre", "We balance softness, durability and impact before a silhouette reaches the sketchbook."],
    ["02", "Design for repeat wear", "Useful details, calm colour and easy proportions allow each piece to live beyond one season."],
    ["03", "Make with care", "Long-term European makers turn each design into a garment with patience and specialist craft."],
    ["04", "Keep the story going", "Thoughtful care, repair and rewear give clothing a longer life and make every resource count."],
  ];

  return (
    <main className="about-page">
      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-hero__mosaic">
          <figure className="about-hero__frame about-hero__frame--lead">
            <img src={leadProduct.image} alt={`${leadProduct.name} dress photographed on a sunlit shoreline`} width="1024" height="1536" />
          </figure>
          <figure className="about-hero__frame about-hero__frame--top">
            <img src={mensShirt.image} alt={`${mensShirt.name} shirt in a Mediterranean coastal setting`} width="1024" height="1536" />
          </figure>
          <figure className="about-hero__frame about-hero__frame--bottom">
            <img src={coastalLayer.image} alt={`${coastalLayer.name} overshirt photographed over coastal wetlands`} width="1024" height="1536" />
          </figure>
        </div>
        <div className="about-hero__content">
          <p className="eyebrow">{siteConfig.brandName} / Our story</p>
          <h1 id="about-title">Two thirds<br />of our world<br />is ocean.</h1>
          <p>{siteConfig.tagline}</p>
          <a className="about-hero__scroll" href="#about-story">Discover our story <FiArrowDown aria-hidden="true" /></a>
        </div>
      </section>

      <section className="about-intro page-shell" id="about-story" aria-labelledby="about-intro-title">
        <div>
          <p className="eyebrow">Ocean at heart</p>
          <h2 id="about-intro-title">Clothing shaped by a slower tide.</h2>
        </div>
        <div className="about-intro__copy">
          <p className="about-intro__lead">{siteConfig.description}</p>
          <p>We started from a simple point of view: what we wear should respect the landscapes that inspire it. The coast gives us our palette, our pace and a daily reminder that every choice is connected.</p>
          <p>That is why we design useful, enduring pieces and work with makers who share our attention to detail. The result is a wardrobe made to feel good now and remain relevant for years.</p>
        </div>
      </section>

      <section className="about-story" aria-labelledby="about-story-title">
        <div className="about-story__image">
          <img src={saleDress.image} alt={`${saleDress.name} dress photographed on a coastal path`} width="1024" height="1536" loading="lazy" />
        </div>
        <div className="about-story__copy">
          <p className="eyebrow">Our beginning</p>
          <h2 id="about-story-title">From Barcelona, looking out to sea.</h2>
          <p>Creativity and the Mediterranean live side by side in our home city. That closeness to the water shaped {siteConfig.brandName}: an independent studio with a belief in fewer, better clothes and a supply chain built on real relationships.</p>
          <blockquote>“The ocean is not a seasonal mood. It is our north star.”</blockquote>
          <span>{siteConfig.logo.symbol} &nbsp; {siteConfig.tagline}</span>
        </div>
      </section>

      <section className="about-principles" aria-labelledby="about-principles-title">
        <header className="about-principles__heading page-shell">
          <div><p className="eyebrow">Our philosophy</p><h2 id="about-principles-title">Positive change,<br />piece by piece.</h2></div>
          <p>Responsibility is a practice, not a finish line. These three commitments guide what we make and how we make it.</p>
        </header>
        <div className="about-principles__grid page-shell">
          {principles.map((principle) => (
            <article className="about-principle" key={principle.number}>
              <div className="about-principle__image"><img src={principle.image} alt={principle.alt} width="1024" height="1536" loading="lazy" /><span aria-hidden="true">{principle.number}</span></div>
              <h3>{principle.title}</h3><p>{principle.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-process page-shell" aria-labelledby="about-process-title">
        <div className="about-process__heading"><p className="eyebrow">How a piece takes shape</p><h2 id="about-process-title">Thoughtful from first fibre to favourite.</h2></div>
        <ol className="about-process__list">
          {process.map(([number, title, copy]) => <li key={number}><span>{number}</span><div><h3>{title}</h3><p>{copy}</p></div></li>)}
        </ol>
      </section>

      <section className="about-values" aria-labelledby="about-values-title">
        <div className="about-values__statement page-shell"><p className="eyebrow">The values we return to</p><h2 id="about-values-title">A simple compass for every decision.</h2></div>
        <div className="about-values__grid page-shell">
          <article><strong>{siteConfig.logo.symbol}</strong><h3>Perspective</h3><p>The ocean connects our choices, communities and future.</p></article>
          <article><strong>01</strong><h3>Shared home</h3><p>One planet, treated as a partner rather than a resource.</p></article>
          <article><strong>03</strong><h3>Core promises</h3><p>Considered materials, respected makers and lasting design.</p></article>
          <article><strong>∞</strong><h3>Beyond seasons</h3><p>Clothes intended for many days, places and chapters.</p></article>
        </div>
      </section>

      <section className="about-makers" aria-labelledby="about-makers-title">
        <div className="about-makers__collage">
          <img className="about-makers__image about-makers__image--large" src={tailoredBottom.image} alt={`${tailoredBottom.name} linen trousers in a sun-washed landscape`} width="1024" height="1536" loading="lazy" />
          <img className="about-makers__image about-makers__image--small" src={mensKnit.image} alt={`${mensKnit.name} knit photographed near the Atlantic`} width="1024" height="1536" loading="lazy" />
        </div>
        <div className="about-makers__copy">
          <p className="eyebrow">Made close, kept close</p><h2 id="about-makers-title">Craft is a relationship.</h2>
          <p>Good clothing begins with people. We choose specialist European makers for their knowledge, then grow together through clear communication, careful sampling and mutual respect.</p>
          <p>Shorter distances make collaboration more direct. Long-term partnerships preserve skills and give every seam, knit and finish the attention it deserves.</p>
          <Link className="text-link" to="/shop">Meet the collection <FiArrowRight aria-hidden="true" /></Link>
        </div>
      </section>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <img src={preorderDress.image} alt={`${preorderDress.name} dress overlooking the sea`} width="1024" height="1536" loading="lazy" />
        <div className="about-cta__overlay" />
        <div className="about-cta__content">
          <p className="eyebrow">Wear the change</p><h2 id="about-cta-title">Choose the pieces that become your story.</h2><p>{siteConfig.tagline}</p>
          <div><Link className="button-light" to="/shop">Explore the collection</Link><Link className="about-cta__link" to="/contact">Talk to our team</Link></div>
        </div>
      </section>
    </main>
  );
}

export default About;
